import { Typography, Box, Divider, Alert, Button } from "@mui/material";
import { CalendarToday as CalendarIcon, Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/common/custom-component/ToastProvider";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import * as em from "common/ErrorMessages";
import { logger } from "helpers/logger";
import { CurrentWorkflowStep } from "../common/constants"
import { fetchCompanyBranchesThunk, fetchTrucksThunk, fetchCompanyUsersThunk, fetchDatabaseRecords, patchDatabaseRecord, unassignUserThunk, fetchTransporterGeneratorThunk } from "../store/thunks/mdsThunks";
import { manageBranchesReducer, initialState } from "../reducers/manageBranchesReducer";
import { useTableColumns } from "../hooks/useTableColumns";
import { getWorkflowsListThunk, startWorkflowProcessThunk } from "store/thunks/workflowThunks";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function ManageBranchesPage() {
    const keycloakAuth = useAuth();
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const { success: ToastSuccess, error: ToastError } = useToast();
    const navigate = useNavigate();

    const [retryTrigger, setRetryTrigger] = useState(0);
    const [error, setError] = useState(null);

    const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

    const workflowList = useSelector((state) => state.workflowList.data);

    const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());
    const [state, dispatchState] = useReducer(manageBranchesReducer, initialState);
    const activeThunksRef = useRef([]);

    const user = keycloakAuth.user;

    const isHeadQuarter = useMemo(() => {
        return !user?.headQuarter;
    }, [user]);

    const displayData = useMemo(() => {
        const list = Array.isArray(state.data.branches) ? state.data.branches : state.data.branches?.payload || [];
        return list.filter((rec) => !deletedRecordIds.has(rec.companyId));
    }, [state.data.branches, deletedRecordIds]);

    const handleRetry = () => {
        setRetryTrigger(prev => prev + 1);
    };

    const errorView = error ? (
        <Box sx={{ p: 4, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
                {error}
            </Alert>
            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRetry}>
                Retry
            </Button>
        </Box>
    ) : null;

    // #region FETCH BRANCHES
    useEffect(() => {
        let promiseRef = null;

        const fetchData = async () => {
            // Reset states when retrying
            setError(null);
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            promiseRef = dispatch(fetchCompanyBranchesThunk({ companyId: selectedCompany.companyId }));

            const recordResult = await promiseRef;

            if (fetchCompanyBranchesThunk.fulfilled.match(recordResult)) {
                const records = recordResult?.payload?.data;
                dispatchState({ type: "SET_BRANCHES", payload: Array.isArray(records) ? records : [] });
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            } else if (fetchCompanyBranchesThunk.rejected.match(recordResult)) {
                // Check if it's not a cancellation
                if (recordResult.payload !== 'cancelled') {
                    setError(recordResult.payload || 'An error occurred while loading data');
                    logger.error('An error occurred while loading data');
                }
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        };

        fetchData();

        // Cleanup: abort on unmount - just like saga!
        return () => {
            if (promiseRef) {
                logger.info('🛑 Component unmounting, aborting request');
                promiseRef.abort();
            }
        };

    }, [dispatch, retryTrigger]);

    useEffect(() => {
        return () => {
            activeThunksRef.current.forEach((thunk) => {
                if (thunk && typeof thunk.abort === "function") thunk.abort();
            });
            activeThunksRef.current = [];
        };
    }, []);

    // #endregion

    // #region SEARCH & PAGINATION
    const { setSearchQuery, filteredData } = useSearchFilter(displayData, ["displayName", "municipality", "address"], null);

    const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

    const pagedData = useMemo(() => {
        const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
        const end = start + state.table.recordsPerPage;
        return (filteredData || []).slice(start, end);
    }, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
    // #endregion

    // #region START WORKFLOW (NEW BRANCH)
    const handleAddClick = useCallback(async () => {
        if (!selectedCompany?.companyId) return;

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const unn = selectedCompany?.companyName?.split('-')?.[0] ?? "";
            const companyName = selectedCompany?.companyName ?? "";
            const headQuarter = selectedCompany?.companyId ?? "";

            const companyUsersData = await dispatch(fetchCompanyUsersThunk({ companyId: selectedCompany?.companyId }));
            const mobileNumber = companyUsersData?.payload?.data?.find(u => u.userId === user?.id)?.mobileNumber ?? "";

            const initialValue = { unn, companyName, headQuarter: headQuarter, selectMobileNumber: mobileNumber };

            const tableData = await dispatch(fetchDatabaseRecords({ tableName: "tbl_page_title" }));
            const pageData = tableData?.payload?.data?.records?.find(item => item.page_name === "/manageBranch");

            if (!pageData) {
                logger.error("Page title info not found.");
                ToastError("Page title info not found.");
                return;
            }
            const pageKey = t(pageData.key);

            const workflowListData = !workflowList ? await dispatch(getWorkflowsListThunk({ userId: user?.id, companyId: selectedCompany.companyId })) : workflowList;
            const parsedTask = Object.values(workflowListData?.payload?.data?.workflowsResponseList || {})
                .map((task) => (typeof task === "string" ? JSON.parse(task) : task))
                .find((task) => task?.key === pageKey);

            if (!parsedTask?.id) {
                logger.error("No matching workflow found for current page key.");
                ToastError("No matching workflow found for current page key.");
                return;
            }


            const formDataResponse = await dispatch(startWorkflowProcessThunk({ definitionKey: parsedTask.id }));

            if (startWorkflowProcessThunk.rejected.match(formDataResponse)) {
                const msg = formDataResponse.payload || "Failed to start new branch workflow.";
                ToastError(msg);
                logger.error("startBranchWorkflowThunk rejected:", formDataResponse.payload);
                return;
            }

            const formData = formDataResponse?.payload?.data?.formData;
            formData.settings = formData.settings || {};
            formData.settings.data = initialValue;

            ToastSuccess("Workflow started");

            localStorage.setItem(CurrentWorkflowStep, JSON.stringify(formData));
            navigate("/formRenderer", { state: { data: formData } });

        } catch (e) {
            logger.error("Unexpected error in handleAddClick:", e);
            ToastError("An unexpected error occurred");
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, selectedCompany?.companyId]);
    // #endregion

    // #region DELETE BRANCH
    const handleDeleteClick = useCallback((row) => {
        dispatchState({
            type: "OPEN_DELETE_DIALOG",
            payload: row,
        });
    }, []);

    const handleCancelDelete = useCallback(() => {
        dispatchState({ type: "CLOSE_DELETE_DIALOG" });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!state.dialogs.selectedRecord?.companyId) {
            dispatchState({ type: "CLOSE_DELETE_DIALOG" });
            return;
        }

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const branchId = state.dialogs.selectedRecord.companyId;
            const keycloakCompanyId = state.dialogs.selectedRecord.keycloakCompanyId;

            // Early returns for clarity
            const companyUsersData = await dispatch(fetchCompanyUsersThunk({ companyId: branchId }));
            if (companyUsersData?.payload?.data?.length > 1) {
                logger.error("There's Existing Users!!");
                ToastError("There's Existing Users!!");
                return;
            }

            const companyTrucksData = await dispatch(fetchTrucksThunk({ companyId: branchId }));
            if (companyTrucksData?.payload?.data?.length > 0) {
                logger.error("There's Existing Trucks!!");
                ToastError("There's Existing Trucks!!");
                return;
            }

            const generatorsCompanyData = await dispatch(fetchTransporterGeneratorThunk({ companyId: branchId }));
            if (generatorsCompanyData?.payload?.data?.length > 0) {
                logger.error("There's Existing Generators!!");
                ToastError("There's Existing Generators!!");
                return;
            }

            // Proceed with unassigning user and patching company
            const unassignUserResponse = await dispatch(unassignUserThunk({ userId: user?.id, companyId: branchId, keycloakCompanyId: keycloakCompanyId }));

            const companyJSON = {
                tableName: "tbl_company",
                fields: {
                    headQuarter: null,
                    softDeleteDate: new Date(),
                    userDeletedId: user?.id,
                },
                matchField: "companyId",
                matchValue: branchId
            };

            const patchDatabaseResponse = await dispatch(patchDatabaseRecord({ JsonToPatch: companyJSON }));

            if (patchDatabaseRecord.fulfilled.match(patchDatabaseResponse)) {
                setDeletedRecordIds((prev) => new Set(prev).add(branchId));
                const records = dispatch(fetchCompanyBranchesThunk({ companyId: selectedCompany.companyId }));
                dispatchState({ type: "SET_BRANCHES", payload: Array.isArray(records) ? records : [] });
                ToastSuccess("Branch deleted successfully");
                dispatchState({ type: "CLOSE_DELETE_DIALOG" });
            } else if (patchDatabaseRecord.rejected.match(patchDatabaseResponse)) {
                const msg = patchDatabaseResponse.payload || "Failed to delete branch.";
                ToastError(msg);
                logger.error("patchDatabaseRecord rejected:", patchDatabaseResponse.payload);
            }
        } catch (e) {
            logger.error("Unexpected error in handleConfirmDelete:", e);
            ToastError("An unexpected error occurred");
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, state.dialogs.selectedRecord, selectedCompany?.companyId]);
    // #endregion

    // #region TABLE COLUMNS
    // Only provide onDelete action when user is headquarters
    const actions = useMemo(() => ({
        ...(isHeadQuarter ? { onDelete: handleDeleteClick } : {}),
    }), [isHeadQuarter, handleDeleteClick]);

    // Use the dynamic columns hook
    const { columns, isLoading: columnsLoading } = useTableColumns('ManageBranches', {
        actions,
    });
    // #endregion

    // #region RETURN
    if (error) return errorView;
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom color="primary">
                {t("ManageBranches")}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {t("Manage branches for your organization")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box
                sx={{
                    my: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <TableActionGroup
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    {isHeadQuarter && (
                        <TableActionButton
                            text={t(em.newBranch)}
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddClick()}
                        />
                    )}
                </TableActionGroup>

                <SearchFilterBar
                    mode="frontend"
                    searchValue={state.table.searchInput}
                    onSearchChange={(v) => {
                        dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
                        setSearchQuery(v);
                    }}
                    showFilterButton={false}
                    showSearchButton={false}
                    searchPlaceholder={t("Search branches...")}
                />
            </Box>


            {!displayData || displayData.length === 0 ? (
                <DataContainer
                    data={[]}
                    columns={columns} columnsLoading={columnsLoading}
                    emptyStateIcon={CalendarIcon}
                    emptyStateTitle={t("No branches found")}
                    emptyStateSubtitle={t("Start by creating a new branch")}
                />
            ) : (
                <>
                    <Box sx={{ p: 2 }}>

                        <DataContainer data={pagedData} columns={columns} columnsLoading={columnsLoading} showRowNumbers maxHeight={600} stickyHeader />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.table.currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            <EnhancedConfirmationDialog
                open={state.dialogs.deleteConfirm}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Confirm Delete")}
                message={t("Are you sure you want to delete this branch? This action cannot be undone.")}
                confirmText={t(em.delete_icon)}
                confirmColor="error"
                cancelText={t(em.Cancel)}
            />

            <LoadingBackdrop
                open={state.loading.isLoadingBackdrop}
                message={t("Loading data, please wait...")}
                variant="bars"
                color="greenGradient"
            />
        </Box>
    );
    // #endregion
}
